import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import { Close, ContactPhone } from '@mui/icons-material';
import { THEME_COLORS, OPERATION_STATUS, UX_TIMING } from '../../constants';
import { SearchBar } from '../common';
import deviceService from '../../services/deviceService';
import ConfirmationModal from './ConfirmationModal';
import { searchContactsPaginated } from '../../services/contactService';
import { processContactsData } from '../../utils/contactUtils';

const DeviceContactsModal = ({ open, onClose, device, onSave }) => {
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [operationStatus, setOperationStatus] = useState(OPERATION_STATUS.IDLE);
  const [showOperationModal, setShowOperationModal] = useState(false);

  // Estado para todos los contactos (solo para mostrar los seleccionados)
  const [allContacts, setAllContacts] = useState([]);
  
  // Unificar estado de búsqueda
  const [searchInput, setSearchInput] = useState('');
  const [lastSearchTerm, setLastSearchTerm] = useState('');

  // Estado para resultados paginados
  const [contactsResults, setContactsResults] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0, hasNext: false, hasPrev: false });
  const [searching, setSearching] = useState(false);

  // Cuando cambia el input
  const handleSearchInputChange = (value) => {
    setSearchInput(value);
  };

  // Buscar contactos paginados
  const handleSearch = useCallback(async () => {
    if (!searchInput || searchInput.trim() === '') {
      setContactsResults([]);
      setPagination({ page: 1, limit: 10, total: 0, pages: 0, hasNext: false, hasPrev: false });
      setLastSearchTerm('');
      return;
    }
    setSearching(true);
    try {
      const result = await searchContactsPaginated(searchInput, 1, 10);
      const processedContacts = processContactsData(result.contacts);
      setContactsResults(processedContacts);
      setPagination(result.pagination);
      setLastSearchTerm(searchInput);
    } catch (err) {
      setContactsResults([]);
      setPagination({ page: 1, limit: 10, total: 0, pages: 0, hasNext: false, hasPrev: false });
      setError(err.message || 'Error buscando contactos');
    } finally {
      setSearching(false);
    }
  }, [searchInput]);

  // Cambiar de página
  const handlePageChange = useCallback(async (newPage) => {
    setSearching(true);
    try {
      const result = await searchContactsPaginated(lastSearchTerm, newPage, pagination.limit);
      const processedContacts = processContactsData(result.contacts);
      setContactsResults(processedContacts);
      setPagination(result.pagination);
    } catch (err) {
      setContactsResults([]);
      setError(err.message || 'Error buscando contactos');
    } finally {
      setSearching(false);
    }
  }, [lastSearchTerm, pagination.limit]);

  // Limpiar búsqueda
  const handleClear = useCallback(() => {
    setContactsResults([]);
    setPagination({ page: 1, limit: 10, total: 0, pages: 0, hasNext: false, hasPrev: false });
    setLastSearchTerm('');
    setSearchInput('');
  }, []);

  // Cargar contactos asociados al dispositivo cuando se abre el modal
  useEffect(() => {
    const fetchDeviceContacts = async () => {
      if (!open || !device) {
        return;
      }

      setLoading(true);
      setError(null);
      
      try {
        const response = await deviceService.getDeviceContactSubscriptions(device.device_id);
        
        if (response.success) {
          // El backend devuelve un array de contactos en response.data
          // Necesitamos extraer los contact_id de cada contacto
          const contacts = response.data || [];
          
          const contactIds = contacts.map(contact => contact.contact_id);
          const contactsSet = new Set(contactIds);
          
          setSelectedContacts(contactsSet);
        } else {
          setError(response.message || 'Error al cargar contactos del dispositivo');
        }
      } catch (error) {
        setError('Error de comunicación al cargar contactos');
      } finally {
        setLoading(false);
      }
    };

    fetchDeviceContacts();
  }, [open, device]);

  useEffect(() => {
    const loadAllContacts = async () => {
      if (open && allContacts.length === 0) {
        try {
          const result = await searchContactsPaginated('', 1, 1000); // Cargar todos los contactos
          const processedContacts = processContactsData(result.contacts);
          
          setAllContacts(processedContacts);
        } catch (error) {
          console.error('[DeviceContactsModal] Error cargando todos los contactos:', error);
        }
      }
    };
    
    loadAllContacts();
  }, [open, allContacts.length]);

  // Filtrar contactos activos (no se usa en este modal, pero se mantiene por compatibilidad)
  const activeContacts = useMemo(() => {
    if (!allContacts || !Array.isArray(allContacts)) {
      return [];
    }
    
    const activeFiltered = allContacts.filter(contact => contact.hasActiveSubscription);
    
    return activeFiltered;
  }, [allContacts]);

  const handleContactToggle = useCallback((contactId) => {
    setSelectedContacts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(contactId)) {
        newSet.delete(contactId);
      } else {
        newSet.add(contactId);
      }
      return newSet;
    });
  }, []);

  const handleSaveContacts = useCallback(async () => {
    if (!device) {
      return;
    }

    const contactIdsArray = Array.from(selectedContacts);
    
    // Mostrar modal de operación
    setOperationStatus(OPERATION_STATUS.LOADING);
    setShowOperationModal(true);

    const startTime = Date.now();

    try {
      const response = await deviceService.updateDeviceContactSubscriptions(
        device.device_id, 
        contactIdsArray
      );

      // Garantizar 1.5s mínimo de UX
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, UX_TIMING.LOADING_MIN_TIME - elapsedTime);
      
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }

      if (response.success) {
        setOperationStatus(OPERATION_STATUS.SUCCESS);
        
        // Mostrar éxito por 2 segundos
        setTimeout(() => {
          setShowOperationModal(false);
          setOperationStatus(OPERATION_STATUS.IDLE);
          
          // Llamar callback de guardado exitoso
          if (onSave) {
            onSave();
          }
          
          // Cerrar modal
          onClose();
        }, UX_TIMING.SUCCESS_DISPLAY_TIME);
      } else {
        setOperationStatus(OPERATION_STATUS.ERROR);
        setError(response.message || 'Error al guardar contactos');
        
        // Ocultar modal después de mostrar error
        setTimeout(() => {
          setShowOperationModal(false);
          setOperationStatus(OPERATION_STATUS.IDLE);
        }, UX_TIMING.ERROR_DISPLAY_TIME);
      }
    } catch (error) {
      setOperationStatus(OPERATION_STATUS.ERROR);
      setError('Error de comunicación al guardar contactos');
      
      // Ocultar modal después de mostrar error
      setTimeout(() => {
        setShowOperationModal(false);
        setOperationStatus(OPERATION_STATUS.IDLE);
      }, UX_TIMING.ERROR_DISPLAY_TIME);
    }
  }, [device, selectedContacts, onSave, onClose]);

  const handleClose = useCallback(() => {
    setSelectedContacts(new Set());
    setError(null);
    setOperationStatus(OPERATION_STATUS.IDLE);
    setShowOperationModal(false);
    onClose();
  }, [onClose]);

  const resetState = useCallback(() => {
    setSelectedContacts(new Set());
    setError(null);
    setOperationStatus(OPERATION_STATUS.IDLE);
    setShowOperationModal(false);
  }, []);

  // Resetear estado cuando se cierre el modal
  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  const selectedCount = selectedContacts.size;
  const deviceName = device?.nombre || 'Dispositivo';

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            border: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
            backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
            boxShadow: `0 8px 32px ${THEME_COLORS.SHADOW_LIGHT}`,
            overflow: 'hidden'
          }
        }}
      >
        {/* Header elegante */}
        <DialogTitle sx={{
          padding: 4,
          paddingBottom: 3,
          backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT,
          borderBottom: `1px solid ${THEME_COLORS.BORDER_LIGHT}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                backgroundColor: THEME_COLORS.TEXT_PRIMARY,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <ContactPhone sx={{ color: THEME_COLORS.WHITE, fontSize: '1.5rem' }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{
                  fontWeight: 600,
                  color: THEME_COLORS.TEXT_PRIMARY,
                  fontSize: '1.25rem'
                }}>
                  Contactos de {deviceName}
                </Typography>
                <Typography variant="body2" sx={{
                  color: THEME_COLORS.TEXT_SECONDARY,
                  fontSize: '0.875rem'
                }}>
                  Selecciona los contactos que recibirán notificaciones
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={handleClose}
              sx={{
                color: THEME_COLORS.TEXT_SECONDARY,
                '&:hover': {
                  backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
                  color: THEME_COLORS.TEXT_PRIMARY
                }
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        {/* Contenido */}
        <DialogContent sx={{ padding: 4, paddingTop: 3 }}>
          {/* Información del dispositivo */}
          <Box sx={{ marginBottom: 3 }}>
            <Typography variant="subtitle1" sx={{
              color: THEME_COLORS.TEXT_PRIMARY,
              fontWeight: 600,
              fontSize: '1rem',
              marginBottom: 1
            }}>
              Información del Dispositivo
            </Typography>
            <Typography variant="body2" sx={{
              color: THEME_COLORS.TEXT_SECONDARY,
              fontSize: '0.875rem'
            }}>
              <strong>Nombre:</strong> {device?.nombre} <br />
              <strong>ID:</strong> {device?.device_id} <br />
              <strong>Tipo:</strong> {device?.device_type}
            </Typography>
          </Box>

          {/* Barra de búsqueda */}
          <Box sx={{ marginBottom: 3 }}>
            <Typography variant="subtitle1" sx={{
              color: THEME_COLORS.TEXT_PRIMARY,
              fontWeight: 600,
              fontSize: '1rem',
              marginBottom: 2
            }}>
              Buscar Contactos
            </Typography>
            <SearchBar
              searchInput={searchInput}
              searchTerm={lastSearchTerm}
              placeholder="Buscar por nombre o número..."
              onSearchInputChange={handleSearchInputChange}
              onSearch={handleSearch}
              onClear={handleClear}
              showButtons={true}
            />
          </Box>

          {/* Indicador de contactos seleccionados */}
          {selectedCount > 0 && (
            <Alert 
              icon={false}
              sx={{ 
                marginBottom: 2,
                borderRadius: '12px',
                backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
                border: `1.5px solid ${THEME_COLORS.TEXT_PRIMARY}`,
                color: THEME_COLORS.TEXT_PRIMARY,
                fontWeight: 500,
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center',
                boxShadow: 'none',
                px: 2,
                py: 1.2,
              }}
            >
              {selectedCount} contacto{selectedCount !== 1 ? 's' : ''} seleccionado{selectedCount !== 1 ? 's' : ''}
            </Alert>
          )}
          {/* Chips de contactos seleccionados mejorados */}
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            mt: 1,
            mb: 2,
            minHeight: 40,
          }}>
            {Array.from(selectedContacts)
              .map(cid => allContacts.find(c => c.contact_id === cid))
              .filter(Boolean)
              .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''))
              .map(contact => (
                <Tooltip key={contact.contact_id} title={`${contact.nombre} (${contact.formatted_celular || contact.celular})`} arrow>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
                      border: `1.5px solid ${THEME_COLORS.TEXT_PRIMARY}`,
                      borderRadius: '16px',
                      px: 2.5,
                      py: 0.5,
                      color: THEME_COLORS.TEXT_PRIMARY,
                      fontWeight: 500,
                      fontSize: '1rem',
                      minHeight: 36,
                      maxWidth: 320,
                      minWidth: 0,
                      overflow: 'hidden',
                      boxShadow: 'none',
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        pr: 1,
                      }}
                    >
                      <Box
                        sx={{
                          fontWeight: 600,
                          color: THEME_COLORS.TEXT_PRIMARY,
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          fontSize: '1rem',
                        }}
                      >
                        {contact.nombre}
                      </Box>
                      <Box
                        sx={{
                          color: THEME_COLORS.TEXT_SECONDARY,
                          fontSize: '0.95rem',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          lineHeight: 1.2,
                        }}
                      >
                        {contact.formatted_celular || contact.celular}
                      </Box>
                    </Box>
                    <IconButton
                      size="small"
                      aria-label="Quitar"
                      onClick={e => {
                        e.stopPropagation();
                        handleContactToggle(contact.contact_id);
                      }}
                      sx={{
                        ml: 1,
                        color: THEME_COLORS.TEXT_PRIMARY,
                        '&:hover': {
                          backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
                          color: THEME_COLORS.TEXT_SECONDARY,
                        },
                        borderRadius: '50%',
                        p: 0.5,
                        minWidth: 0,
                        minHeight: 0,
                      }}
                    >
                      <Close sx={{ fontSize: 22 }} />
                    </IconButton>
                  </Box>
                </Tooltip>
              ))}
          </Box>

          {/* Lista de contactos paginada */}
          <Box sx={{ marginBottom: 2 }}>
            <Typography variant="subtitle1" sx={{
              color: THEME_COLORS.TEXT_PRIMARY,
              fontWeight: 600,
              fontSize: '1rem',
              marginBottom: 2
            }}>
              Contactos Disponibles
            </Typography>

            {searching && (
              <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
                <CircularProgress size={32} sx={{ color: THEME_COLORS.TEXT_PRIMARY }} />
              </Box>
            )}

            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  marginBottom: 2,
                  borderRadius: '12px'
                }}
              >
                {error}
              </Alert>
            )}

            {!searching && !error && (
              <>
                {!lastSearchTerm || lastSearchTerm.trim() === '' ? (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: THEME_COLORS.TEXT_SECONDARY,
                      fontStyle: 'italic',
                      textAlign: 'center',
                      padding: 4
                    }}
                  >
                    Usa la barra de búsqueda y el botón Buscar para encontrar contactos
                  </Typography>
                ) : contactsResults.length === 0 ? (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: THEME_COLORS.TEXT_SECONDARY,
                      fontStyle: 'italic',
                      textAlign: 'center',
                      padding: 4
                    }}
                  >
                    No se encontraron contactos con "{lastSearchTerm}"
                  </Typography>
                ) : (
                  <>
                    <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                      {contactsResults.map(contact => (
                        <ListItem
                          key={contact.contact_id}
                          sx={{
                            borderRadius: '12px',
                            marginBottom: 1,
                            border: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
                            backgroundColor: selectedContacts.has(contact.contact_id) 
                              ? `${THEME_COLORS.TEXT_PRIMARY}08`
                              : THEME_COLORS.BACKGROUND_PAPER,
                            '&:hover': {
                              backgroundColor: `${THEME_COLORS.TEXT_PRIMARY}12`
                            },
                            cursor: 'pointer'
                          }}
                          onClick={() => handleContactToggle(contact.contact_id)}
                        >
                          <ListItemText
                            primary={contact.nombre}
                            secondary={contact.formatted_celular || contact.celular}
                            primaryTypographyProps={{
                              sx: { 
                                fontWeight: 500,
                                color: THEME_COLORS.TEXT_PRIMARY
                              }
                            }}
                            secondaryTypographyProps={{
                              sx: { 
                                color: THEME_COLORS.TEXT_SECONDARY,
                                fontSize: '0.875rem'
                              }
                            }}
                          />
                          <ListItemSecondaryAction>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={selectedContacts.has(contact.contact_id)}
                                  onChange={() => handleContactToggle(contact.contact_id)}
                                  sx={{
                                    color: THEME_COLORS.TEXT_SECONDARY,
                                    '&.Mui-checked': {
                                      color: THEME_COLORS.TEXT_PRIMARY
                                    }
                                  }}
                                />
                              }
                              label=""
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                    {/* Paginación */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={!pagination.hasPrev}
                        onClick={() => handlePageChange(pagination.page - 1)}
                        sx={{ color: THEME_COLORS.TEXT_SECONDARY, borderColor: THEME_COLORS.BORDER_LIGHT, borderRadius: '12px', minWidth: 100 }}
                      >
                        Anterior
                      </Button>
                      <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY, alignSelf: 'center' }}>
                        Página {pagination.page} de {pagination.pages || 1}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={!pagination.hasNext}
                        onClick={() => handlePageChange(pagination.page + 1)}
                        sx={{ color: THEME_COLORS.TEXT_SECONDARY, borderColor: THEME_COLORS.BORDER_LIGHT, borderRadius: '12px', minWidth: 100 }}
                      >
                        Siguiente
                      </Button>
                    </Box>
                  </>
                )}
              </>
            )}
          </Box>
        </DialogContent>

        {/* Botones */}
        <DialogActions sx={{
          backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT,
          borderTop: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
          p: 4,
          pt: 3,
          gap: 2,
          justifyContent: 'flex-end'
        }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            size="large"
            sx={{
              color: THEME_COLORS.TEXT_SECONDARY,
              borderColor: THEME_COLORS.BORDER_LIGHT,
              borderRadius: '12px',
              px: 4,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 500,
              minWidth: 120
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSaveContacts}
            variant="contained"
            size="large"
            disabled={loading}
            sx={{
              backgroundColor: THEME_COLORS.TEXT_PRIMARY,
              color: THEME_COLORS.BACKGROUND_PAPER,
              borderRadius: '12px',
              px: 4,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 140,
              '&:hover': {
                backgroundColor: '#1a1a1a',
                transform: 'translateY(-1px)'
              }
            }}
          >
            Guardar Contactos
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de operación */}
      <ConfirmationModal
        open={showOperationModal}
        status={operationStatus}
        title={
          operationStatus === OPERATION_STATUS.LOADING 
            ? 'Guardando Contactos'
            : operationStatus === OPERATION_STATUS.SUCCESS 
              ? '¡Contactos Guardados!'
              : 'Error'
        }
        subtitle={
          operationStatus === OPERATION_STATUS.LOADING 
            ? 'Actualizando la lista de contactos del dispositivo...'
            : operationStatus === OPERATION_STATUS.SUCCESS 
              ? `Los contactos de ${deviceName} se han actualizado exitosamente.`
              : error || 'Hubo un problema al guardar los contactos'
        }
        userName={deviceName}
        hideActions={true}
      />
    </>
  );
};

export default DeviceContactsModal;
